import React, { useEffect, useState } from "react";
import { XCrossIcon } from "../../../../assets";
import { AddEditRegionModalProps } from "../types/RegionTypes";
import { SingleDropdown } from "../../../../components/shared";
import { isEqual } from "lodash";
import { getApi, postApi, putApi } from "../../../../apis/api";
import { useMessage } from "../../../../context/MessageContext";
import {
  GENERAL_ERROR_MESSAGE,
  SERVICE_CONFIG_MESSAGES,
} from "../../../../utils/message";
import { ADD_REGION_ENDPOINTS, GET_STATE_LISTING, UPDATE_REGION_ENDPOINTS } from "../../../../apis/end-points";
import { Loader } from "../../../../components/loaders/loaders";


const AddEditRegionModal: React.FC<AddEditRegionModalProps> = ({
  isEdit = false,
  isOpen,
  onClose,
  initialData = null,
  onSuccess,
}) => {

  const [data, setData] = useState({
    state: "",
    region: "",
  });

  const { setMessage } = useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [stateList, setStateList] = useState<{ id: string; name: string }[]>([]);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [initialPayload, setInitialPayload] = useState<any>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  // Fetch state list when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchStates = async () => {
      try {
        setDataLoading(true);
        const response = await getApi<any[]>(`${GET_STATE_LISTING}?limit=500&status=2`);
        if (response.success) {
          const mapped = (response.data || []).map((item: any) => ({
            id: item.id,
            name: item.state,
          }));
          setStateList(mapped);
        }
      } catch (error) {
        console.error("Failed to fetch states", error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchStates();
  }, [isOpen]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit || !initialData) {
      const empty = { state: "", region: "" };
      setData(empty);
      setInitialPayload(empty);
      setSelectedState(null);
      return;
    }

    const payload = {
      state: initialData.state || "",
      region: initialData.region || "",
    };

    setData(payload);
    setInitialPayload(payload);
    setSelectedState(
      initialData.state
        ? stateList.find(o => o.name?.toLowerCase() === initialData.state?.toLowerCase()) || null
        : null
    );
  }, [isEdit, initialData, isOpen, stateList]);


  // Track changes for edit mode
  useEffect(() => {
    if (!isEdit || !initialPayload) return;

    const currentPayload = {
      state: data.state,
      region: data.region,
    };

    setIsChanged(!isEqual(initialPayload, currentPayload));
  }, [data, initialPayload, isEdit]);

  const resetForm = () => {
    setData({ state: "", region: "" });
    setSelectedState(null);
    setStateList([]);
    setInitialPayload(null);
    setIsChanged(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    const payload = {
      state: data.state.trim(),
      region: data.region.trim(),
    };

    try {
      setLoading(true);
      let apiResponse;

      if (isEdit && initialData?.id) {
        apiResponse = await putApi(`${UPDATE_REGION_ENDPOINTS}${initialData.id}`, payload);
      } else {
        apiResponse = await postApi(ADD_REGION_ENDPOINTS, payload);
      }

      if (apiResponse?.success) {
        setMessage(
          isEdit
            ? SERVICE_CONFIG_MESSAGES.REGION_UPDATE_SUCCESS
            : SERVICE_CONFIG_MESSAGES.REGION_ADD_SUCCESS,
          "success"
        );
        onSuccess?.();
        handleClose();
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
      console.error("Region save failed", error);
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;

  return (
    <div className="modal modal--hazmatDetail modal--open">
       {(loading || dataLoading) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ' rgba(17, 25, 39, 0.5);',
            }}>
              <Loader />
            </div>
          )}
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">

          <div className="modal__header">
            <h4 className="modal__header--title">
              {isEdit ? "Edit Region" : "Add New Region"}
            </h4>
            <button
              className="btn btn__transparent btnClose"
              onClick={handleClose}
              type="button"
            >
              <XCrossIcon />
            </button>
          </div>

          <div className="modal__body modal-body-left">
            <div className="formRow formRow__2">
              {/* State Dropdown - First Field */}
              <div className="formCol">
                <div className="form__Field">
                  <SingleDropdown
                    fieldName="state"
                    selectedItem={
                      typeof data.state === "string"
                        ? stateList.find(o => o.name?.toLowerCase() === data.state?.toLowerCase()) || null
                        : selectedState || null
                    }
                    setSelectedData={(_, item) => {
                      setSelectedState(item);
                      setData((prev) => ({
                        ...prev,
                        state: item?.name || "",
                      }));
                    }}
                    staticData={stateList}
                    labelTitle="State"
                    placeholder="Select State"
                    keyBindName="name"
                    mandatory={true}
                    enableRemove={true}
                  />
                </div>
              </div>

              {/* Region Text Input - Second Field */}
              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="region" className="form__label">
                    Region <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="region"
                    type="text"
                    placeholder="Enter Region"
                    className="form__input form__input--sm"
                    value={data.region}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, region: e.target.value.trimStart() }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal__footer">
            <div className="btn__group">
              <button
                className="btn btn__white"
                onClick={handleClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn btn__primary"
                onClick={handleSave}
                type="button"
                disabled={!data.state || !data.region || (isEdit && !isChanged)}
              >
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal__backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default AddEditRegionModal;
